import { createContext, type ReactElement, type ReactNode, useContext } from 'react';

import type { ExecutionMode } from '../../api';

/**
 * Product switches read from the runtime config (`/config.js`), so turning one on needs no new
 * build (P8 R4, Proposal, to be validated).
 */
export interface Features {
  /**
   * The Confirmation execution mode can be chosen on E5 and E7. Off until the confirmation
   * request is sent to the trader (register Q187, W7 R28): P8 R1.
   */
  readonly confirmationModeEnabled: boolean;
}

/**
 * An execution mode as C1 writes it: upper case in the settings routes (`CONFIRMATION`), lower
 * case in `GET /me` (`confirmation`).
 */
type AnyExecutionMode = ExecutionMode['mode'] | Lowercase<ExecutionMode['mode']>;

// TODO(Q257): Julien confirms hiding the Confirmation mode until the Q187 flow exists.
/**
 * True when `mode` cannot be chosen now: the Confirmation mode while the P8 switch is off
 * (P8 R1, Proposal, to be validated). The single rule the settings form and the Home row share.
 */
export function isHiddenMode(mode: AnyExecutionMode, features: Features): boolean {
  return mode.toUpperCase() === 'CONFIRMATION' && !features.confirmationModeEnabled;
}

/** What a screen sees with no provider: every switch off, the safe side (P8 R4). */
export const FEATURES_OFF: Features = { confirmationModeEnabled: false };

const FeaturesContext = createContext<Features>(FEATURES_OFF);

/** Makes the {@link Features} of the runtime config available to every screen. */
export function FeaturesProvider({
  features,
  children,
}: {
  readonly features: Features;
  readonly children: ReactNode;
}): ReactElement {
  return <FeaturesContext value={features}>{children}</FeaturesContext>;
}

/** The product switches of the running app. */
export function useFeatures(): Features {
  return useContext(FeaturesContext);
}
